// src/components/customers/EditCustomer.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

// Constants for better maintainability
const EDITING_TIPS = [
  'Keep customer information up to date',
  'Accurate phone numbers ensure successful notifications',
  'Email addresses enable receipt delivery'
];

const EditCustomer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [customerNotFound, setCustomerNotFound] = useState(false);
  const [customerActivity, setCustomerActivity] = useState({
    totalOrders: 0,
    totalSpent: 0,
    lastOrder: null
  });
  const [apiError, setApiError] = useState(null);

  // Debug logging
  useEffect(() => {
    // console.log('EditCustomer mounted with ID:', id);
    // console.log('Current isFetching state:', isFetching);
  }, [id, isFetching]);

  // Memoized helper functions
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  }, []);

  // Memoized customer activity display data
  const activityDisplay = useMemo(() => [
    { label: 'Total Orders', value: customerActivity.totalOrders || 0 },
    { label: 'Total Spent', value: formatCurrency(customerActivity.totalSpent) },
    { label: 'Last Order', value: formatDate(customerActivity.lastOrder) }
  ], [customerActivity.totalOrders, customerActivity.totalSpent, customerActivity.lastOrder, formatCurrency, formatDate]);

  // Data fetching
  useEffect(() => {
    const fetchCustomer = async () => {
      console.log('Starting fetchCustomer with ID:', id);
      
      try {
        setIsFetching(true);
        setCustomerNotFound(false);
        setApiError(null);

        console.log('Making API request to:', `/getCustomer/${id}`);
        
        const response = await api.get(`${API_CONFIG.CUSTOMERS.GET_CUSTOMER}/${id}`);
        console.log('API Response received:', response);
        console.log('Response data:', response.data);
        
        const responseData = response.data;
        
        if (responseData.success && responseData.customer) {
          const customerData = responseData.customer;
          console.log('Customer data received:', customerData);
          
          setFormData({
            name: customerData.customer_name || '',
            email: customerData.customer_email || '',
            phone: customerData.customer_phone || '',
            company: customerData.company_name || ''
          });
          
          // Set customer activity data if available
          setCustomerActivity({
            totalOrders: customerData.total_orders || 0,
            totalSpent: customerData.total_spent || 0,
            lastOrder: customerData.last_order || null
          });
          
          console.log('Form data and activity set successfully');
        } else {
          console.log('Customer not found in response');
          setCustomerNotFound(true);
          showToast(responseData.error || 'Customer not found', 'error');
        }
      } catch (error) {
        console.error('Error fetching customer:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response,
          request: error.request
        });
        
        const errorMessage = error.response?.data?.error || 'Failed to load customer data';
        const errorDetails = error.response?.data?.details;
        
        setApiError(errorMessage);
        
        if (error.response?.status === 404) {
          console.log('Customer not found (404)');
          setCustomerNotFound(true);
          showToast(errorMessage, 'error');
        } else if (error.response?.status === 400) {
          console.log('Bad request (400)');
          showToast(errorMessage, 'error');
          if (errorDetails) {
            console.error('Validation errors:', errorDetails);
          }
        } else if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
          console.log('Network error');
          showToast('Network error: Please check your connection', 'error');
        } else if (error.response?.status === 500) {
          console.log('Server error (500)');
          showToast('Server error: Please try again later', 'error');
        } else {
          console.log('Other error');
          showToast(errorMessage, 'error');
        }
      } finally {
        console.log('Fetch completed, setting isFetching to false');
        setIsFetching(false);
      }
    };

    if (id) {
      fetchCustomer();
    } else {
      console.log('No ID provided, setting isFetching to false');
      setIsFetching(false);
      setCustomerNotFound(true);
    }
  }, [id]);

  // Event handlers
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showToast('Customer name is required', 'error');
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      console.log('Updating customer with data:', formData);
      const response = await api.put(`/updateCustomer/${id}`, {
        customer_name: formData.name.trim(),
        customer_email: formData.email.trim() || null,
        customer_phone: formData.phone.trim() || null,
        company_name: formData.company.trim() || null
      });

      const responseData = response.data;
      console.log('Update response:', responseData);
      
      if (responseData.success) {
        showToast('Customer updated successfully', 'success');
        navigate('/store#customers');
      } else {
        const errorMsg = responseData.error || 'Failed to update customer';
        setApiError(errorMsg);
        showToast(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      const errorMessage = error.response?.data?.error || 'An error occurred while updating customer';
      const errorDetails = error.response?.data?.details;
      
      setApiError(errorMessage);
      
      if (error.response?.status === 400) {
        // Validation errors
        if (errorDetails) {
          showToast(`${errorMessage}: ${Array.isArray(errorDetails) ? errorDetails.join(', ') : errorDetails}`, 'error');
        } else {
          showToast(errorMessage, 'error');
        }
      } else if (error.response?.status === 404) {
        showToast('Customer not found', 'error');
        setCustomerNotFound(true);
      } else {
        showToast(errorMessage, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [formData, id, navigate]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear API error when user starts typing
    if (apiError) {
      setApiError(null);
    }
  }, [apiError]);

const handleDelete = useCallback(async () => {
  // Create a custom confirmation dialog
  const showDeleteConfirmation = () => {
    return new Promise((resolve) => {
      // Create overlay
      const overlay = document.createElement('div');
      overlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
      
      // Create dialog
      const dialog = document.createElement('div');
      dialog.className = 'bg-white rounded-xl shadow-lg max-w-md w-full mx-auto transform transition-all';
      
      dialog.innerHTML = `
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-gray-900" style="font-size: 16px;">Delete Customer</h3>
              <p class="text-gray-600 mt-1" style="font-size: 14px;">This action cannot be undone</p>
            </div>
          </div>
          
          <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p class="text-red-800 font-medium" style="font-size: 14px;">You're about to delete:</p>
            <p class="text-red-700 mt-1" style="font-size: 14px;">"${formData.name}"</p>
            <p class="text-red-600 mt-2" style="font-size: 13px;">All customer data will be permanently removed from the system.</p>
          </div>
          
          <div class="flex flex-col sm:flex-row gap-3 justify-end">
            <button 
              id="cancelDelete" 
              class="px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-white transition-colors flex-1 sm:flex-none"
              style="font-size: 14px;"
            >
              Cancel
            </button>
            <button 
              id="confirmDelete" 
              class="px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors flex-1 sm:flex-none"
              style="font-size: 14px;"
            >
              Delete Customer
            </button>
          </div>
        </div>
      `;
      
      overlay.appendChild(dialog);
      document.body.appendChild(overlay);
      
      // Add event listeners
      const cancelBtn = document.getElementById('cancelDelete');
      const confirmBtn = document.getElementById('confirmDelete');
      
      const cleanup = () => {
        document.body.removeChild(overlay);
      };
      
      cancelBtn.onclick = () => {
        cleanup();
        resolve(false);
      };
      
      confirmBtn.onclick = () => {
        cleanup();
        resolve(true);
      };
      
      // Close on overlay click
      overlay.onclick = (e) => {
        if (e.target === overlay) {
          cleanup();
          resolve(false);
        }
      };
      
      // Close on Escape key
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          cleanup();
          document.removeEventListener('keydown', handleEscape);
          resolve(false);
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      
      // Focus the cancel button for accessibility
      cancelBtn.focus();
    });
  };

  const shouldDelete = await showDeleteConfirmation();
  if (!shouldDelete) {
    return;
  }

  // Continue with delete logic...
  try {
    const response = await api.delete(`/deleteCustomer/${id}`);
    const responseData = response.data;
    
    if (responseData.success) {
      showToast('Customer deleted successfully', 'success');
      navigate('/store#customers');
    } else {
      const errorMsg = responseData.error || 'Failed to delete customer';
      showToast(errorMsg, 'error');
    }
  } catch (error) {
    console.error('Error deleting customer:', error);
    const errorMessage = error.response?.data?.error || 'An error occurred while deleting customer';
    const errorDetails = error.response?.data?.details;
    
    if (error.response?.status === 400) {
      if (errorDetails) {
        showToast(`${errorMessage}: ${errorDetails}`, 'error');
      } else {
        showToast(errorMessage, 'error');
      }
    } else if (error.response?.status === 404) {
      showToast('Customer not found', 'error');
      setCustomerNotFound(true);
    } else {
      showToast(errorMessage, 'error');
    }
  }
}, [formData.name, id, navigate]);

  const handleBackClick = useCallback(() => {
    navigate('/store');
  }, [navigate]);

  // Loading component
  const LoadingState = () => (
    <div className="flex justify-center items-center min-h-64">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mx-auto mb-3"></div>
        <p className="text-gray-600" style={{ fontSize: '14px' }}>Loading customer details...</p>
        <p className="text-gray-400 text-xs mt-2">ID: {id}</p>
      </div>
    </div>
  );

  // Customer not found component
  const CustomerNotFound = () => (
    <div className="min-h-full bg-white flex items-center justify-center py-12">
      <div className="max-w-md w-full text-center">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Customer Not Found</h2>
          <p className="text-gray-600 mb-6" style={{ fontSize: '14px' }}>
            {apiError || "The customer you're looking for doesn't exist or you don't have permission to view it."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/store"
              className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              style={{ fontSize: '14px' }}
            >
              Back to Customers
            </Link>
            <Link
              to="/store#customers-new"
              className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-white transition-colors"
              style={{ fontSize: '14px' }}
            >
              Add New Customer
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  // Error banner component
  const ErrorBanner = () => {
    if (!apiError) return null;

    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800 mb-1">Error</h3>
            <p className="text-sm text-red-700">{apiError}</p>
          </div>
          <button
            onClick={() => setApiError(null)}
            className="text-red-400 hover:text-red-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // Customer header component
  const CustomerHeader = () => (
    <div className="flex items-center mb-6">
      <div className="flex-shrink-0 w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
        <span className="text-xl font-bold text-blue-800">
          {formData.name?.charAt(0) || 'C'}
        </span>
      </div>
      <div className="ml-4 min-w-0 flex-1">
        <h1 className="text-lg font-semibold text-gray-900 truncate" style={{ fontSize: '18px' }}>{formData.name}</h1>
        <p className="text-gray-500 truncate" style={{ fontSize: '14px' }}>Customer ID: {id}</p>
      </div>
    </div>
  );

  // Customer activity component
  const CustomerActivitySection = () => (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-4" style={{ fontSize: '16px' }}>Customer Activity</h3>
      <div className="space-y-4">
        {activityDisplay.map((activity, index) => (
          <div key={index} className="flex justify-between items-center">
            <span className="text-gray-600" style={{ fontSize: '14px' }}>{activity.label}:</span>
            <span className="font-semibold text-gray-900" style={{ fontSize: '14px' }}>
              {activity.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  // Tips component
  const TipsSection = () => (
    <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
      <div className="flex items-start mb-3">
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
          <span className="text-blue-600 text-sm">💡</span>
        </div>
        <h3 className="text-sm font-semibold text-blue-800" style={{ fontSize: '15px' }}>Editing Tips</h3>
      </div>
      <ul className="text-blue-700 space-y-2" style={{ fontSize: '14px' }}>
        {EDITING_TIPS.map((tip, index) => (
          <li key={index} className="flex items-start">
            <span className="text-blue-500 mr-2 mt-1">•</span>
            {tip}
          </li>
        ))}
      </ul>
    </div>
  );

  // Danger zone component
  const DangerZone = () => (
    <div className="bg-red-50 rounded-xl border border-red-200 p-6">
      <div className="flex items-start mb-4">
        <svg className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <div>
          <h3 className="text-sm font-semibold text-red-800 mb-1" style={{ fontSize: '15px' }}>Danger Zone</h3>
          <p className="text-red-700" style={{ fontSize: '14px' }}>
            Once you delete a customer, there is no going back. Please be certain.
          </p>
        </div>
      </div>
      <button 
        onClick={handleDelete}
        className="w-full px-4 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ fontSize: '14px' }}
        disabled={isLoading}
      >
        {isLoading ? 'Deleting...' : 'Delete Customer'}
      </button>
    </div>
  );



  if (isFetching) {
    console.log('Rendering LoadingState');
    return <LoadingState />;
  }

  if (customerNotFound) {
    console.log('Rendering CustomerNotFound');
    return <CustomerNotFound />;
  }

  console.log('Rendering main form');
  return (
    <div className="min-h-full bg-white">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-semibold text-gray-900" style={{ fontSize: '18px' }}>
                Edit Customer
              </h1>
              <p className="text-gray-600 mt-1" style={{ fontSize: '14px' }}>
                Update customer information
              </p>
            </div>
            <button
              onClick={handleBackClick}
              className="flex items-center text-blue-600 hover:text-blue-700 transition-colors font-medium whitespace-nowrap"
              style={{ fontSize: '14px' }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              Back to Customers
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Error Banner */}
            <ErrorBanner />

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <CustomerHeader />
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  {/* Full Name */}
                  <div>
                    <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '14px' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="customerName"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter customer's full name"
                      required
                      style={{ fontSize: '14px' }}
                      maxLength={100}
                    />
                    <div className="text-right mt-1">
                      <span className="text-gray-500" style={{ fontSize: '12px' }}>
                        {formData.name.length}/100 characters
                      </span>
                    </div>
                  </div>
                  
                  {/* Email Address */}
                  <div>
                    <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '14px' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="customerEmail"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="customer@example.com"
                      style={{ fontSize: '14px' }}
                    />
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label htmlFor="customerPhone" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '14px' }}>
                      Phone Number
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                        </svg>
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        id="customerPhone"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+234 800 000 0000"
                        style={{ fontSize: '14px' }}
                      />
                    </div>
                  </div>
                  
                  {/* Company Name */}
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '14px' }}>
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      id="companyName"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company name (optional)"
                      style={{ fontSize: '14px' }}
                      maxLength={100}
                    />
                    <div className="text-right mt-1">
                      <span className="text-gray-500" style={{ fontSize: '12px' }}>
                        {formData.company.length}/100 characters
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button 
                    type="submit" 
                    disabled={isLoading || !formData.name.trim()}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{ fontSize: '15px' }}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        Update Customer
                      </div>
                    )}
                  </button>
                  <button 
                    type="button" 
                    onClick={handleBackClick}
                    disabled={isLoading}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{ fontSize: '15px' }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>

            {/* Danger Zone */}
            <DangerZone />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            <CustomerActivitySection />
            <TipsSection />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCustomer;